<script lang="ts" setup>
import { XIcon } from '@clicksign/excalibur';
import { MaybeRefOrGetter } from '@vueuse/core';
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';

import { BrandButton } from '~~base-ui';
import { isFetchError, useModals, trackElapsedTime, useFeature, Decisions } from '~~core';

import { useFetchSigner } from '~~/domains/requirement/repositories/signer';
import { useFetchConfig } from '~~/domains/requirement/repositories/liveness';
import { Provider } from '~~/domains/requirement/constants/caf';

import CameraPhotoPreview from '../../../../components/CameraPhotoPreview/CameraPhotoPreview.vue';
import SkeletonGenericRequirement from '../../../../components/SkeletonGenericRequirement/SkeletonGenericRequirement.vue';
import { loadFaceLivenessSDK } from '../../../../utils/CAF';
import { useRequirementStep } from '../../../../composables/use-requirement-step';
import { useSubSteps } from '../../../../composables/use-sub-steps';
import { LivenessCarousel1Svg, PhotoAttachedDocumentSvg, AllowNavigatorUseCameraSvg } from '../../../../assets/svg';
import { LivenessCameraModal, LivenessCarousel, LivenessModalHelp } from '../../../../components';
import { saveRequirement } from '../../../../services';
import { requirementQueryKeys, useFetchRequirement } from '../../../../repositories/requirement';
import { useRequirement } from '../../../../composables/use-requirement';
import { useFetchCAFCredentials } from '../../../../repositories/caf';

const enum SubStep {
  Tutorial = 'before-capture',
  Capture = 'capture',
}

function useBinaryArray<const T, const K>(flag: MaybeRefOrGetter<boolean>, trueOption: T, falseOption: K) {
  return computed(() => (toValue(flag) ? trueOption : falseOption));
}

const modalName = {
  help: 'help',
};

const STEP_NAME = 'liveness';

// TOGGLE_LIVENESS_WITH_STRATEGY_ENABLED
// TODO: remove useFeature when the feature flag is removed
const { isEnabled } = useFeature(Decisions.TOGGLE_LIVENESS_WITH_STRATEGY_ENABLED);

const { t } = useI18n();
const { finishRequirement, signatureRequestKey, setActiveRequirement } = useRequirementStep();
const { signer } = useFetchSigner(signatureRequestKey);
const { requirement, isLoading: isQueryLoading } = useFetchRequirement(STEP_NAME, signatureRequestKey);
const { credentials } = useFetchCAFCredentials(signatureRequestKey);
const { isFilled, artefact } = useRequirement(requirement);
const { isModalOpen, openModal, closeModal } = useModals();
const { getTimeElapsed } = trackElapsedTime();
const { config } = useFetchConfig(signatureRequestKey, isEnabled);
const queryClient = useQueryClient();
const toast = useToast();
const gtm = useGtm();

setActiveRequirement(STEP_NAME);

const title = t('requirement.liveness.title');
const isLoading = ref(false);
const isSaving = ref(false);
const pageControlData = reactive({
  carouselCurrentStep: 0,
});

const carouselItems = computed(() => [
  {
    image: 'carousel-1',
    title: t('requirement.liveness.carousel.step1.title'),
    text: t('requirement.liveness.carousel.step1.text'),
    svgIllustration: LivenessCarousel1Svg,
  },
  {
    image: 'carousel-2',
    title: t('requirement.liveness.carousel.step2.title'),
    text: [
      { icon: 'exclamation-circle', text: t('requirement.facialBiometrics.carousel.step2.text.first') },
      { icon: 'user', text: t('requirement.facialBiometrics.carousel.step2.text.second') },
      { icon: 'lightbulb-on', text: t('requirement.facialBiometrics.carousel.step2.text.fourth') },
    ],
    svgIllustration: PhotoAttachedDocumentSvg,
  },
  {
    image: 'carousel-3',
    title: t('requirement.liveness.carousel.step3.title'),
    text: t('requirement.liveness.carousel.step3.text'),
    svgIllustration: AllowNavigatorUseCameraSvg,
  },
]);

const artefactUrl = computed(() => artefact.value?.imageUrl);
const hasValidEvidence = computed(() => !!artefactUrl.value);
const subSteps = useBinaryArray(hasValidEvidence, [SubStep.Capture], [SubStep.Tutorial, SubStep.Capture]);
const { isSubStep, nextSubStep, previousSubStep } = useSubSteps(subSteps);

useHead({ title });
loadFaceLivenessSDK();

const isNextCode = computed(() => config.value?.livenessProvider === Provider.NEXT_CODE);

function handleTutorialNext() {
  if (pageControlData.carouselCurrentStep === 2) {
    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_name: 'LivenessStart',
      ga_event_category: isNextCode.value ? 'nextCode' : 'caf',
      ga_event_label: 'liveness',
    });

    nextSubStep();
    isLoading.value = true;
  }

  if (pageControlData.carouselCurrentStep < 2) pageControlData.carouselCurrentStep += 1;
}

function handleModalClose() {
  isLoading.value = false;
  previousSubStep();
}

async function completeRequirement(evidenceKey?: string) {
  if (isFilled.value || !evidenceKey) return finishRequirement();

  isSaving.value = true;

  try {
    await saveRequirement(STEP_NAME, signatureRequestKey.value, { livenessKey: evidenceKey });

    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_name: isNextCode.value ? 'LivenessNextCode' : 'LivenessCAF',
      ga_event_category: isNextCode.value ? 'NextCodeTimestamp' : 'CafTimestamp',
      ga_event_label: 'tempo em milissegundos',
      ga_event_action: 'timing',
      ga_event_value: getTimeElapsed(),
    });

    queryClient.invalidateQueries(requirementQueryKeys.show(signatureRequestKey.value, STEP_NAME));
    finishRequirement();
  } catch (error) {
    toast.error(t('requirement.liveness.error.saveEvidence'));
    isSaving.value = false;

    if (!isFetchError(error)) throw error;
  }
}

function handleCapture(evidenceKey: string) {
  isLoading.value = false;

  completeRequirement(evidenceKey);
}

const canShowNextCode = computed(() => isEnabled.value);
</script>

<template>
  <section
    :class="$style.root"
    @keyup.enter.stop
  >
    <SkeletonGenericRequirement v-if="isQueryLoading || !credentials || !signer" />

    <CameraPhotoPreview
      v-else-if="hasValidEvidence && artefactUrl"
      :picture="artefactUrl"
      icon="user"
      :saving="isSaving"
      :icon-text="$t('requirement.common.face')"
      readonly
      disable-go-back
      unknown-size
      :class="$style.preview"
      @advance="completeRequirement()"
    >
      <h2>
        {{ $t('requirement.liveness.preview.title') }}
      </h2>
    </CameraPhotoPreview>

    <template v-else>
      <LivenessCarousel
        :items="carouselItems"
        :selected="pageControlData.carouselCurrentStep"
        @back="pageControlData.carouselCurrentStep -= 1"
        @next="pageControlData.carouselCurrentStep += 1"
      />

      <div
        v-if="isSubStep(SubStep.Capture)"
        :class="$style.capture"
      >
        <LivenessCameraModal
          :person-id="signer.key"
          :token="credentials.token"
          :environment="credentials.environment"
          :signature-request-key="signatureRequestKey"
          :requirement="STEP_NAME"
          :provider="config?.livenessProvider"
          :can-show-next-code="canShowNextCode"
          @close="handleModalClose"
          @capture="handleCapture"
        />
      </div>

      <div :class="$style.stepControl">
        <BrandButton
          full-width
          :loading="isLoading"
          @click="handleTutorialNext"
        >
          {{ $t('requirement.liveness.actions.next') }}

          <XIcon
            v-if="pageControlData.carouselCurrentStep !== 2"
            right
            icon="arrow-right"
          />
        </BrandButton>

        <BrandButton
          design="link"
          @click="openModal(modalName.help)"
        >
          {{ t('requirement.liveness.knowMore') }}
        </BrandButton>
      </div>
    </template>

    <LivenessModalHelp
      v-if="isModalOpen(modalName.help)"
      :requirement="STEP_NAME"
      @close="closeModal()"
    />
  </section>
</template>

<style lang="scss" module>
.root {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 496px;
  margin: 0 auto;
}

.stepControl {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: calc(100% - var(--space-regular));
  margin: var(--space-medium) 0 var(--space-small-xx);
  gap: var(--space-regular);
}

.capture {
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
}

.preview {
  width: 100%;
}
</style>
